import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { UI, SERVICES_FR, CITIES_FR, MANIFESTO_FR, FAQ_FR, BUSINESS_FR } from "@/lib/i18n";
import { SERVICES, CITIES, MANIFESTO, FAQ, BUSINESS } from "@/lib/data";

const LangContext = createContext(null);

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "en";
    return localStorage.getItem("nb.lang") || "en";
  });

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = (l) => {
    setLangState(l);
    try {
      localStorage.setItem("nb.lang", l);
    } catch (e) {}
  };

  const toggle = () => setLang(lang === "en" ? "fr" : "en");

  const value = useMemo(() => {
    const ui = UI[lang] || UI.en;

    // Localized services
    const services = SERVICES.map((s) => {
      if (lang === "fr" && SERVICES_FR[s.slug]) {
        return {
          ...s,
          _en: { name: s.name, tagline: s.tagline, summary: s.summary },
          name: SERVICES_FR[s.slug].name,
          tagline: SERVICES_FR[s.slug].tagline,
          summary: SERVICES_FR[s.slug].summary,
        };
      }
      return s;
    });

    // Localized cities (intro only, other fields stay accurate as proper nouns)
    const cities = CITIES.map((c) => {
      if (lang === "fr" && CITIES_FR[c.slug]) {
        return { ...c, intro: CITIES_FR[c.slug] };
      }
      return c;
    });

    const manifesto = lang === "fr" ? MANIFESTO_FR : MANIFESTO;
    const faq = lang === "fr" ? FAQ_FR : FAQ;

    const business =
      lang === "fr"
        ? { ...BUSINESS, address: BUSINESS_FR.address, license: BUSINESS_FR.license }
        : BUSINESS;

    return { lang, setLang, toggle, ui, services, cities, manifesto, faq, business };
  }, [lang]);

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang must be used within LangProvider");
  return ctx;
}
